import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Tenant AI provider + key management card (Admin > Branding tab).
 *
 * Owns: primary provider/model/key, fallback provider/model/key, monthly
 * budget, and a mini usage sparkline over the last 30 days.
 *
 * Extracted from Admin.js as part of the Feb 2026 admin refactor.
 */
export function TenantAiKeyCard({ tenantId }) {
  const [info, setInfo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [val, setVal] = useState("");
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-4o");
  const [fbProvider, setFbProvider] = useState("");
  const [fbModel, setFbModel] = useState("");
  const [fbKey, setFbKey] = useState("");
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/ai-key`).then(({ data }) => {
      setInfo(data);
      setProvider(data.ai_provider || "openai");
      setModel(data.ai_model || "gpt-4o");
      setFbProvider(data.ai_provider_fallback || "");
      setFbModel(data.ai_model_fallback || "");
    }).catch(() => setInfo(null));
    api.get(`/tenants/${tenantId}/ai-budget`).then(({ data }) => setBudget(String(data.budget_usd || ""))).catch(() => {});
    api.get(`/tenants/${tenantId}/ai-usage?days=30`).then(({ data }) => setUsage(data)).catch(() => setUsage(null));
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ai_api_key: val || "********", ai_provider: provider, ai_model: model,
                        ai_provider_fallback: fbProvider, ai_model_fallback: fbModel };
      if (fbKey) payload.ai_api_key_fallback = fbKey;
      await api.put(`/tenants/${tenantId}/ai-key`, payload);
      toast.success(val ? "Tenant AI key saved" : "AI provider updated");
      setVal(""); setFbKey(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const saveBudget = async () => {
    try {
      await api.put(`/tenants/${tenantId}/ai-budget`, { budget_usd: parseFloat(budget) || 0 });
      toast.success("Budget saved"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const clear = async () => {
    if (!window.confirm("Remove this tenant's AI key? AI features will fall back to the platform key (unless the policy is Required).")) return;
    try { await api.delete(`/tenants/${tenantId}/ai-key`); toast.success("Tenant AI key removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!info) return null;
  const providers = info.providers || {};
  const modelsForProvider = providers[provider]?.models || [];
  const providerCanUseEmergent = providers[provider]?.emergent;
  const series = usage?.series || [];
  const maxTokens = Math.max(1, ...series.map((s) => s.tokens_est || 0));
  const fmt = (n) => (n || 0).toLocaleString();
  const budgetPct = usage?.budget_pct || 0;
  const budgetTone = budgetPct >= 100 ? "danger" : budgetPct >= 80 ? "warning" : "ocean";

  return (
    <div className="glass-card p-5 md:col-span-2" data-testid="tenant-ai-key-card">
      <h3 className="font-display text-lg text-[color:var(--ink)] mb-1">Tenant AI provider &amp; key</h3>
      <p className="text-[11px] text-[color:var(--ink-3)] mb-3">
        Policy: <span className={`font-medium ${info.ai_key_policy === "mandatory" ? "text-[color:var(--danger)]" : "text-[color:var(--ink)]"}`}>{info.ai_key_policy === "mandatory" ? "Required" : "Optional (falls back to platform key)"}</span>
      </p>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-xs text-[color:var(--ink-3)]">Provider</Label>
          <select data-testid="tenant-ai-provider" value={provider} onChange={(e) => { setProvider(e.target.value); setModel(providers[e.target.value]?.models?.[0] || ""); }}
            className="mt-1.5 w-full h-9 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-3 text-sm">
            {Object.entries(providers).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-[color:var(--ink-3)]">Model</Label>
          <select data-testid="tenant-ai-model" value={model} onChange={(e) => setModel(e.target.value)}
            className="mt-1.5 w-full h-9 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-3 text-sm">
            {modelsForProvider.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      {!providerCanUseEmergent && (
        <p className="text-[10px] text-[color:var(--warning,#f97316)] mb-2">⚡ {providers[provider]?.label} requires you to paste your own API key below — the platform key doesn&apos;t cover it.</p>
      )}

      {info.has_key ? (
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-1 rounded bg-[color:var(--success)]/10 text-[color:var(--success)] text-[11px] font-medium">Key configured ({info.masked})</span>
          <Button data-testid="tenant-ai-key-clear" size="sm" variant="outline" onClick={clear} className="h-7 text-xs">Remove</Button>
        </div>
      ) : (
        info.ai_key_policy === "mandatory" && <p className="text-[11px] text-[color:var(--danger)] mb-2">AI features are disabled for this tenant until a key is added.</p>
      )}
      <Label className="text-xs text-[color:var(--ink-3)]">API key {providerCanUseEmergent && "(leave blank to use platform key)"}</Label>
      <div className="flex items-center gap-2 mt-1.5">
        <Input data-testid="tenant-ai-key-input" type="password" value={val} onChange={(e) => setVal(e.target.value)}
          placeholder={info.has_key ? "Paste a new key to rotate" : `${providers[provider]?.label} API key`}
          className="bg-[color:var(--surface)]" />
        <Button data-testid="tenant-ai-key-save" onClick={save} disabled={saving} className="bg-[color:var(--brand-ocean)] text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>

      {/* Fallback provider (kicks in if the primary provider errors out) */}
      <div className="mt-4 p-3 rounded-lg bg-[color:var(--surface-2)] border border-[color:var(--glass-border)]" data-testid="tenant-ai-fallback">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h4 className="text-xs font-semibold text-[color:var(--ink)]">Fallback model</h4>
            <p className="text-[10px] text-[color:var(--ink-3)]">If the primary provider errors, retry once with this one so AI features stay up.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="tenant-ai-fb-provider" value={fbProvider} onChange={(e) => { setFbProvider(e.target.value); setFbModel(providers[e.target.value]?.models?.[0] || ""); }}
            className="h-8 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2 text-xs">
            <option value="">— none —</option>
            {Object.entries(providers).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select data-testid="tenant-ai-fb-model" value={fbModel} onChange={(e) => setFbModel(e.target.value)} disabled={!fbProvider}
            className="h-8 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-2 text-xs disabled:opacity-50">
            <option value="">— model —</option>
            {(providers[fbProvider]?.models || []).map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        {fbProvider && !providers[fbProvider]?.emergent && (
          <Input data-testid="tenant-ai-fb-key" type="password" value={fbKey} onChange={(e) => setFbKey(e.target.value)}
            placeholder={info.has_fallback_key ? "Paste a new fallback key to rotate" : `${providers[fbProvider]?.label} API key`}
            className="mt-2 h-8 text-xs bg-[color:var(--surface)]" />
        )}
      </div>

      {/* AI budget */}
      <div className="mt-5 pt-4 border-t border-[color:var(--glass-border)]" data-testid="tenant-ai-budget">
        <div className="flex items-center gap-2 justify-between mb-2">
          <div>
            <h4 className="text-sm font-semibold text-[color:var(--ink)]">Monthly spend budget</h4>
            <p className="text-[10px] text-[color:var(--ink-3)]">Get an in-app alert when the estimated AI spend crosses this cap. Set to 0 to disable.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[color:var(--ink-3)]">$</span>
          <Input data-testid="tenant-ai-budget-input" type="number" min="0" step="0.5" value={budget}
            onChange={(e) => setBudget(e.target.value)}
            className="w-32 h-8 bg-[color:var(--surface)]" />
          <Button data-testid="tenant-ai-budget-save" size="sm" onClick={saveBudget} variant="outline" className="h-8 text-xs">Save budget</Button>
          {usage?.budget_usd > 0 && (
            <span className={`text-[11px] font-medium ${budgetTone === "danger" ? "text-[color:var(--danger)]" : budgetTone === "warning" ? "text-[color:var(--warning,#f97316)]" : "text-[color:var(--ink)]"}`}>
              ${(usage.month_usd_est || 0).toFixed(2)} of ${usage.budget_usd.toFixed(2)} ({budgetPct.toFixed(1)}%)
            </span>
          )}
        </div>
        {usage?.budget_usd > 0 && (
          <div className="h-1.5 mt-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
            <div className={`h-full ${budgetTone === "danger" ? "bg-[color:var(--danger)]" : budgetTone === "warning" ? "bg-[color:var(--warning,#f97316)]" : "bg-[color:var(--brand-ocean)]"}`}
              style={{ width: `${Math.min(100, budgetPct)}%` }} />
          </div>
        )}
      </div>

      {usage && (
        <div className="mt-5 pt-4 border-t border-[color:var(--glass-border)]" data-testid="tenant-ai-usage">
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <h4 className="text-sm font-semibold text-[color:var(--ink)]">AI usage · last {usage.days} days</h4>
            <span className="text-[10px] text-[color:var(--ink-3)]">est. ${usage.usd_per_million_tokens.toFixed(2)}/M tokens</span>
          </div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="p-2 rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)]" data-testid="ai-usage-requests">
              <div className="text-[9px] text-[color:var(--ink-3)] uppercase tracking-wide">Requests</div>
              <div className="text-base font-bold text-[color:var(--ink)] tabular-nums">{fmt(usage.total_requests)}</div>
            </div>
            <div className="p-2 rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)]" data-testid="ai-usage-tokens">
              <div className="text-[9px] text-[color:var(--ink-3)] uppercase tracking-wide">Tokens (est.)</div>
              <div className="text-base font-bold text-[color:var(--ink)] tabular-nums">{fmt(usage.total_tokens_est)}</div>
            </div>
            <div className="p-2 rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)]" data-testid="ai-usage-cost">
              <div className="text-[9px] text-[color:var(--ink-3)] uppercase tracking-wide">Cost (est.)</div>
              <div className="text-base font-bold text-[color:var(--brand-ocean)] tabular-nums">${usage.total_usd_est.toFixed(2)}</div>
            </div>
            <div className="p-2 rounded bg-[color:var(--surface)] border border-[color:var(--glass-border)]" data-testid="ai-usage-errors">
              <div className="text-[9px] text-[color:var(--ink-3)] uppercase tracking-wide">Errors</div>
              <div className={`text-base font-bold tabular-nums ${usage.total_errors > 0 ? "text-[color:var(--danger)]" : "text-[color:var(--ink)]"}`}>{fmt(usage.total_errors)}</div>
            </div>
          </div>
          {series.length > 0 ? (
            <div className="flex items-end gap-0.5 h-16" data-testid="ai-usage-sparkline">
              {series.slice(-30).map((s) => {
                const h = Math.max(2, Math.round((s.tokens_est / maxTokens) * 100));
                return (
                  <div key={s.date} className="flex-1 flex flex-col items-center" title={`${s.date}: ${fmt(s.tokens_est)} tok · ${s.requests} req · $${(s.usd_est || 0).toFixed(3)}`}>
                    <div className="w-full rounded-t bg-[color:var(--brand-ocean)] transition-colors hover:bg-[color:var(--brand-mint)]" style={{ height: `${h}%` }} />
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-[11px] text-[color:var(--ink-3)] text-center py-3">No AI calls yet in this window.</p>
          )}
        </div>
      )}
    </div>
  );
}
